import { components } from '@feedfm/clips-services-api';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Chip, IconButton, Link, TableCell, Tooltip, Typography } from '@mui/material';
import { Explicitness } from '../Explicitness';
import { LazyThumbnail } from '../LazyThumbnail';
import type { ColumnId } from './CatalogTable.types';
import * as styles from './CatalogTable.styles';
import { formatDate, getStatusBadge, STATUS_BADGE } from './CatalogTable.utils';

type TrackObject = components['schemas']['TrackObject'];

interface CatalogTableCellsProps {
  columnId: ColumnId;
  track: TrackObject;
  isPlaying: boolean;
  onPlayTrack: (track: TrackObject) => void;
  onApplyFilter: (key: string, value: string) => void;
}

export const CatalogTableCells = ({
  columnId,
  track,
  isPlaying,
  onPlayTrack,
  onApplyFilter,
}: CatalogTableCellsProps) => {
  switch (columnId) {
    case 'artwork':
      return (
        <TableCell key={columnId}>
          <Box sx={styles.artworkContainer}>
            <LazyThumbnail
              src={track.artwork_thumbnail_url}
              fallbackSrc={track.artwork_url}
              alt="Album cover"
              sx={styles.artworkImage}
              placeholder={
                <Box sx={styles.artworkPlaceholder}>
                  <Typography variant="caption" color="text.secondary">
                    No Art
                  </Typography>
                </Box>
              }
            />

            <IconButton
              size="small"
              className="play-button"
              onClick={(e) => {
                e.stopPropagation();
                if (track.track_preview?.url) {
                  onPlayTrack(track);
                }
              }}
              disabled={!track.track_preview?.url}
              sx={styles.playButton(isPlaying)}
            >
              {isPlaying ? (
                <PauseIcon sx={styles.playButtonIcon} />
              ) : (
                <PlayArrowIcon sx={styles.playButtonIcon} />
              )}
            </IconButton>
          </Box>
        </TableCell>
      );

    case 'title':
      return (
        <TableCell key={columnId}>
          <Tooltip title={track.title || ''}>
            <Typography variant="body1" sx={styles.titleCellTruncate}>
              {track.title}
            </Typography>
          </Tooltip>
        </TableCell>
      );

    case 'artist':
      return (
        <TableCell key={columnId}>
          <Link
            component="button"
            variant="body1"
            onClick={(e) => {
              e.stopPropagation();
              onApplyFilter('artistNameIs', track.display_artist_name || '');
            }}
            sx={styles.clickableLink}
          >
            {track.display_artist_name}
          </Link>
        </TableCell>
      );

    case 'release':
      return (
        <TableCell key={columnId}>
          <Link
            component="button"
            variant="body1"
            onClick={(e) => {
              e.stopPropagation();
              onApplyFilter('releaseNameIs', track.release_title || '');
            }}
            sx={styles.clickableLink}
          >
            {track.release_title}
          </Link>
        </TableCell>
      );

    case 'release_year':
      return (
        <TableCell key={columnId}>
          <Typography variant="body2" color="text.primary">
            {track.release_year}
          </Typography>
        </TableCell>
      );

    case 'create_date':
      return (
        <TableCell key={columnId}>
          <Typography variant="body2" color="text.primary">
            {formatDate(track.create_date)}
          </Typography>
        </TableCell>
      );

    case 'include_date':
      return (
        <TableCell key={columnId}>
          <Typography variant="body2" color="text.primary">
            {track.include_date ? formatDate(track.include_date) : '-'}
          </Typography>
        </TableCell>
      );

    case 'status': {
      const status = getStatusBadge(track);
      return (
        <TableCell key={columnId}>
          <Chip
            label={status}
            size="small"
            color={
              status === STATUS_BADGE.LIVE
                ? 'success'
                : status === STATUS_BADGE.STAGED
                  ? 'warning'
                  : 'default'
            }
          />
        </TableCell>
      );
    }

    case 'collections':
      return (
        <TableCell key={columnId}>
          <Tooltip
            title={
              track.collections && track.collections.length > 0
                ? track.collections.map((col) => col.name).join(', ')
                : ''
            }
            placement="top"
            arrow
          >
            <Box sx={styles.collectionsContainer}>
              {track.collections?.slice(0, 3).map((col) => (
                <Chip
                  key={col.id}
                  label={col.name}
                  size="small"
                  sx={styles.collectionChip}
                />
              ))}
              {track.collections && track.collections.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  +{track.collections.length - 3} more
                </Typography>
              )}
            </Box>
          </Tooltip>
        </TableCell>
      );

    case 'bpm':
      return (
        <TableCell key={columnId}>
          <Typography variant="body2" color="text.primary">
            {track.bpm}
          </Typography>
        </TableCell>
      );

    case 'genre':
      return (
        <TableCell key={columnId}>
          <Tooltip
            title={
              track.genres && track.genres.length > 0
                ? track.genres.map((g) => g.name).join(', ')
                : ''
            }
            placement="top"
            arrow
          >
            <Box sx={styles.metadataCellContainer}>
              {track.genres && track.genres.length > 0 ? (
                <>
                  {track.genres.slice(0, 3).map((genre) => (
                    <Chip
                      key={genre.id}
                      label={genre.name}
                      size="small"
                      sx={styles.metadataChipClickable}
                      onClick={(e) => {
                        e.stopPropagation();
                        onApplyFilter('genreIs', genre.name);
                      }}
                    />
                  ))}
                  {track.genres.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{track.genres.length - 3} more
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  None
                </Typography>
              )}
            </Box>
          </Tooltip>
        </TableCell>
      );

    case 'explicitness': {
      const explicitnessValue =
        track.explicitness === 'Explicit'
          ? 'explicit'
          : track.explicitness === 'ExplicitContentEdited'
            ? 'censored'
            : null;
      return (
        <TableCell key={columnId}>
          <Box sx={styles.metadataCellContainer}>
            {explicitnessValue && (
              <Explicitness explicitness={explicitnessValue} />
            )}
          </Box>
        </TableCell>
      );
    }

    case 'rating':
      return (
        <TableCell key={columnId}>
          <Box sx={styles.metadataCellContainer}>
            {track.rating && track.rating !== 'unknown' ? (
              <Chip
                label={track.rating}
                size="small"
                sx={styles.metadataChip}
                color={
                  track.rating === 'G'
                    ? 'success'
                    : track.rating === 'PG'
                      ? 'info'
                      : track.rating === 'PG-13'
                        ? 'warning'
                        : track.rating === 'R' || track.rating === 'NC17'
                          ? 'error'
                          : 'default'
                }
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            )}
          </Box>
        </TableCell>
      );

    case 'vibes':
      return (
        <TableCell key={columnId}>
          <Box sx={styles.metadataCellContainer}>
            {track.categories?.Vibes && track.categories.Vibes.length > 0 ? (
              track.categories.Vibes.map((vibe, idx) => (
                <Chip
                  key={`vibe-${idx}`}
                  label={vibe}
                  size="small"
                  sx={styles.metadataChip}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            )}
          </Box>
        </TableCell>
      );

    case 'decade':
      return (
        <TableCell key={columnId}>
          <Box sx={styles.metadataCellContainer}>
            {track.categories?.Decade &&
            track.categories.Decade.length > 0 ? (
              track.categories.Decade.map((decade, idx) => (
                <Chip
                  key={`decade-${idx}`}
                  label={decade}
                  size="small"
                  sx={styles.metadataChip}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            )}
          </Box>
        </TableCell>
      );

    case 'holiday':
      return (
        <TableCell key={columnId}>
          <Box sx={styles.metadataCellContainer}>
            {track.categories?.Holiday &&
            track.categories.Holiday.length > 0 ? (
              track.categories.Holiday.map((holiday, idx) => (
                <Chip
                  key={`holiday-${idx}`}
                  label={holiday}
                  size="small"
                  sx={styles.metadataChip}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            )}
          </Box>
        </TableCell>
      );

    case 'occasion':
      return (
        <TableCell key={columnId}>
          <Box sx={styles.metadataCellContainer}>
            {track.categories?.Occasion &&
            track.categories.Occasion.length > 0 ? (
              track.categories.Occasion.map((occasion, idx) => (
                <Chip
                  key={`occasion-${idx}`}
                  label={occasion}
                  size="small"
                  sx={styles.metadataChip}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            )}
          </Box>
        </TableCell>
      );

    case 'gain':
      return (
        <TableCell key={columnId}>
          <Box sx={styles.metadataCellContainer}>
            {track.track_preview?.gain !== null &&
            track.track_preview?.gain !== undefined ? (
              <Typography variant="body2">
                {typeof track.track_preview.gain === 'number'
                  ? `${track.track_preview.gain.toFixed(2)}dB`
                  : `${track.track_preview.gain}dB`}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            )}
          </Box>
        </TableCell>
      );

    case 'isrc':
      return (
        <TableCell key={columnId}>
          <Typography
            component="span"
            variant="body2"
            sx={styles.isrcText(!!(track as any).isrc)}
          >
            {(track as any).isrc || '-'}
          </Typography>
        </TableCell>
      );

    case 'duration':
      return (
        <TableCell key={columnId}>
          <Typography variant="body1" color="text.primary">
            {track.duration_in_seconds
              ? `${Math.floor(track.duration_in_seconds / 60)}:${String(
                  track.duration_in_seconds % 60,
                ).padStart(2, '0')}`
              : '-'}
          </Typography>
        </TableCell>
      );

    default:
      return null;
  }
};
